import {
  ChineseSurface,
  CorporateShell,
  I18nProvider,
  siteUrlFor,
  type NavEntry,
  type SubsidiaryLink,
  useDocument,
  useI18n,
} from '@longnan/ui';
import { biOf, useT } from './i18n';
import type { EcosystemDoc } from './types';
import { Home } from './pages/Home';
import { About } from './pages/About';
import { Ecosystem } from './pages/Ecosystem';
import { Leadership } from './pages/Leadership';
import { News } from './pages/News';
import { Esg } from './pages/Esg';
import { Ir } from './pages/Ir';
import { Careers } from './pages/Careers';
import { Contact } from './pages/Contact';
import { Publications } from './pages/Publications';

type PageId = 'publications' | 'home' | 'about' | 'leadership' | 'ecosystem' | 'news' | 'news-detail' | 'esg' | 'ir' | 'careers' | 'contact';

const ROUTES: Record<string, PageId> = {
  '/': 'home',
  '/about': 'about',
  '/about/leadership': 'leadership',
  '/ecosystem': 'ecosystem',
  '/news': 'news',
  '/esg': 'esg',
  '/ir': 'ir',
  '/careers': 'careers',
  '/contact': 'contact',
  '/publications': 'publications',
};

/** 静态路由 + 动态路由（/news/:id）。 */
function resolve(path: string): { id: PageId | 'notfound'; param?: string } {
  const exact = (ROUTES as Record<string, PageId | undefined>)[path];
  if (exact) return { id: exact };
  const m = /^\/news\/([^/]+)$/.exec(path);
  if (m) return { id: 'news-detail', param: decodeURIComponent(m[1]) };
  return { id: 'notfound' };
}

function Shell() {
  const { path, locale } = useI18n();
  const { t } = useT();
  const eco = useDocument<EcosystemDoc>('group', 'ecosystem.json');

  const subs: SubsidiaryLink[] = (eco.data?.subsidiaries ?? []).map((s) => ({
    key: s.key,
    name: s.shortName,
    url: siteUrlFor(s.key, locale),
  }));

  const nav: NavEntry[] = [
    { to: '/about', label: biOf('nav.about') },
    { to: '/about/leadership', label: biOf('nav.leadership') },
    { to: '/ecosystem', label: biOf('nav.ecosystem'), children: subs },
    { to: '/news', label: biOf('nav.news') },
    { to: '/esg', label: biOf('nav.esg') },
    { to: '/ir', label: biOf('nav.ir') },
    { to: '/careers', label: biOf('nav.careers') },
    { to: '/publications', label: biOf('nav.publications') },
    { to: '/contact', label: biOf('nav.contact') },
  ];

  const route = resolve(path);
  const id = route.id;

  return (
    <CorporateShell
      brand={{ name: biOf('brand.name'), mark: t('brand.mark') }}
      nav={nav}
      homeLabel={biOf('brand.home')}
      subsidiaries={subs}
      footerNote={biOf('footer.note')}
      footerSite={t('footer.site')}
      skipLabel={biOf('skip')}
    >
      {id === 'home' && <Home />}
      {id === 'about' && <About />}
      {id === 'ecosystem' && <Ecosystem />}
      {id === 'leadership' && <Leadership />}
      {id === 'news' && <News />}
      {id === 'news-detail' && <News id={route.param} />}
      {id === 'esg' && <Esg />}
      {id === 'ir' && <Ir />}
      {id === 'careers' && <Careers />}
      {id === 'contact' && <Contact />}
      {id === 'publications' && <Publications />}
      {id === 'notfound' && (
        <div className="mx-auto max-w-6xl px-5 py-24">
          <h1 className="text-3xl font-semibold text-body">{t('notfound.title')}</h1>
          <p className="mt-3 text-muted">
            <code className="text-body">{path}</code> — {t('notfound.body')}
          </p>
        </div>
      )}
    </CorporateShell>
  );
}

export function App() {
  return (
    <I18nProvider site="group">
      <ChineseSurface>
        <Shell />
      </ChineseSurface>
    </I18nProvider>
  );
}
